import React, {Fragment, useMemo, useState} from "react";
import {Box, styled, Typography} from "@mui/material";
import {useTranslation} from "react-i18next";
import {
  ChurchOutlined,
  FavoriteBorderRounded,
  WorkspacesOutlined
} from "@mui/icons-material";
import {animated, useSpring} from '@react-spring/web';
import {useInView} from 'react-intersection-observer';
import useScrollDirection from "../hooks/useScrollDirection.hook";
import useIsMobile from "../hooks/useIsMobile.hook";

export const Title = styled(Typography)(({theme}) => ({
  color: '#161722',
  textTransform: 'uppercase',
  textAlign: 'center',
  marginTop: 32,
  marginBottom: 16,
  fontWeight: 700,
  [theme.breakpoints.up('xs')]: {
    fontSize: 26,
  },
  [theme.breakpoints.up('sm')]: {
    fontSize: 28,
  },
  [theme.breakpoints.up('md')]: {
    fontSize: 30,
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: 32,
  },
  [theme.breakpoints.up('xl')]: {
    fontSize: 34,
  },
}));

const CardsContainer = styled(Box)(({theme}) => ({
  marginTop: 40,
  display: "flex",
  justifyContent: "space-between",
  gap: 40,
  [theme.breakpoints.up('xs')]: {
    flexDirection: 'column',
  },
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
  },
}));

const Card = styled(Box)(() => ({
  position: 'relative',
  borderRadius: 10,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: 16,
  gap: 28,
}));

const AnimatedCard = animated(Card);

const getMobileAnimation = inView => ({
  from: {opacity: 0, transform: 'translateX(-100%)'},
  to: {opacity: inView ? 1 : 0, transform: inView ? 'translateX(0%)' : 'translateX(100%)'},
  config: {duration: 400},
  reset: true,
});

const CardComponent = ({Icon, title, text, style}) => {
  return (
    <AnimatedCard style={style}>
      <Icon sx={{fontSize: 56}}/>
      <Typography variant="body1" fontWeight={600} letterSpacing={4}>{title}</Typography>
      <Typography variant="subtitle2" textAlign="center" letterSpacing={0.5}>
        {text}
      </Typography>
    </AnimatedCard>
  );
};

const CardAbout = () => {
  const {t} = useTranslation();
  const [ref, inView] = useInView({triggerOnce: false, threshold: 0.1});
  const animation = useSpring(getMobileAnimation(inView));

  return (
    <Box width="fit-content" ref={ref}>
      <CardComponent
        Icon={ChurchOutlined}
        title={t('about.about-us.title')}
        text={t('about.about-us.text')}
        style={animation}
      />
    </Box>
  );
};

const CardInternal = () => {
  const {t} = useTranslation();
  const [ref, inView] = useInView({triggerOnce: false, threshold: 0.1});
  const isMobile = useIsMobile();

  const animation = useSpring(
    isMobile
      ? getMobileAnimation(inView)
      : {
        from: {opacity: 0},
        to: {opacity: inView ? 1 : 0},
        config: {duration: 400},
        reset: true,
      }
  );

  return (
    <Box width="fit-content" ref={ref}>
      <CardComponent
        Icon={WorkspacesOutlined}
        title={t('about.internal.title')}
        text={t('about.internal.text')}
        style={animation}
      />
    </Box>
  );
};

const CardExternal = () => {
  const {t} = useTranslation();
  const [ref, inView] = useInView({triggerOnce: false, threshold: 0.1});
  const isMobile = useIsMobile();

  const animation = useSpring(
    isMobile
      ? getMobileAnimation(inView)
      : {
        from: {opacity: 0, transform: 'translateX(100%)'},
        to: {opacity: inView ? 1 : 0, transform: inView ? 'translateX(0%)' : 'translateX(100%)'},
        config: {duration: 400},
        reset: true,
      }
  );

  return (
    <Box width="fit-content" ref={ref}>
      <CardComponent
        Icon={FavoriteBorderRounded}
        title={t('about.external.title')}
        text={t('about.external.text')}
        style={animation}
      />
    </Box>
  );
};

const AboutSection = () => {
  const {t} = useTranslation();

  return (
    <Box sx={{padding: '40px 16px 100px 16px'}} display="flex" flexDirection="column" alignItems="center">
      <Box maxWidth={1200} width="100%">
        <Title variant="h4">{t('about.title')}</Title>
        <CardsContainer>
          <CardAbout/>
          <CardInternal/>
          <CardExternal/>
        </CardsContainer>
      </Box>
    </Box>
  );
};

export default AboutSection;
